import { useState, useEffect } from 'react';
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";

import { StatusService } from '../../services/StatusService';

import Toast from "../../helpers/Toast";
import { GetStatusWithPipeDTO } from '../../models/Status/GetStatusWithPipeDTO';
import { StatusUpdateDTO } from '../../models/Status/StatusUpdateDTO';
import { StatusNavBar } from '../../components/Status/StatusNavBar';
import { Col, Row } from 'reactstrap';
import Layout from '../../components/Layout/Layout';
import PageTitle from '../../components/Layout/PageTitle';
import ButtonComponent from '../../components/ButtonComponent';
import InputComponent from '../../components/InputComponent';
import useValidationSchema, { FieldConfig } from "../../components/Validations";

export default function StatusEdit() {
    const { id } = useParams<{ id: string }>();
    const intl = useIntl();
    const [baseStatusName, setBaseStatusName] = useState<string>("");

    const [pageTitle, setPageTitle] = useState("Estado");

    const [pipes, setPipes] = useState<{ id: number; name: string }[]>([]);
    const [status, setStatus] = useState<GetStatusWithPipeDTO>({} as GetStatusWithPipeDTO);
    const [loadingUpdate, setLoadingUpdate] = useState<boolean>(false);
    const statusService = new StatusService();

    const currentUrl = window.location.href;
    const [firstUrl, setFirstUrl] = useState<string>('');

    const fields: FieldConfig[] = [
    ["frontendName","string","Screens.User.Edit.Validation.FrontendName"],
    ["statusEnumValue","string","Screens.User.Edit.Validation.StatusEnumValue"],
    ["idPipe","number","Screens.User.Edit.Validation.Pipe"]
    ];

    const schemaStatusValidation = useValidationSchema(fields);
    
    useEffect(() => {
    const fetchPipes = async () => {
        const response = await statusService.GetAllPipes();
        if (response.success && response.obj) {
            setPipes(response.obj.map(pipe => ({
                id: pipe.id,
                name: pipe.name
            })));
        }
    };

    fetchPipes();
    }, []);

    const fetchData = async () => {
        
        var response = await statusService.GetStatusById(String(id));

        if (response.success === false || response.obj == null) {
            Toast.Show("error", response.message);
            return;
        }

        setBaseStatusName(response.obj.name);
        setStatus(response.obj);
    };

    useEffect(() => {
        if (id !== undefined && id !== null) {
            fetchData();
        }
    }, [id]);

    useEffect(() => {
        if(status?.id !== undefined){
            setPageTitle(`Estado ${status?.name}`);
        }
    }, [status?.id]);

    //Effects to load all the page once someone searchs a status in NextPossibleStatusNavBar
    if (!firstUrl) {
        setFirstUrl(window.location.href);
    }
    useEffect(() => {
        if(firstUrl && firstUrl !== currentUrl){
            window.location.replace(currentUrl);
        }
    }, [firstUrl, currentUrl]);

    const validateUniqueFields = async () => {
    const allStatusResponse = await statusService.GetAllStatus();
    if (!allStatusResponse.success || !allStatusResponse.obj) {
        Toast.Show("error", "Erro ao validar a unicidade dos campos.");
        return false;
    }

    const allStatus = allStatusResponse.obj as GetStatusWithPipeDTO[];

    const existsSameFrontendName = allStatus.some(s => s.frontendName?.toLowerCase() === status.frontendName?.toLowerCase() && s.id !== status.id);
    const existsSameEnumValue = allStatus.some(s => s.statusEnumValue?.toLowerCase() === status.statusEnumValue?.toLowerCase() && s.id !== status.id);

    if (existsSameFrontendName || existsSameEnumValue) {
        let message = "Erro de validação:\n";
        if (existsSameFrontendName) message += "Nome já utilizado.\n";
        if (existsSameEnumValue) message += "Valor de Estado já utilizado.\n";
        Toast.Show("error", message);
        return false;
    }

    return true;
    };

    const editStatus = async () => {
        setLoadingUpdate(true);
    
        const isValid = await validateUniqueFields();
        if (!isValid) {
        setLoadingUpdate(false);
        return;
        }

        const updatedStatus: StatusUpdateDTO = {
            id: status.id,
            name: status.name,
            active: status.active,
            idPipe: status.idPipe, 
            statusEnumValue: status.statusEnumValue,
            frontendName: status.frontendName,
            frontendOrder: status.frontendOrder,
            color: status.color
        };
    
        const { error } = schemaStatusValidation.validate(updatedStatus);
        if (error) {
            Toast.Show("error", error.details.map(detail => detail.message).join(', '));
            setLoadingUpdate(false);
            return;
        }
    
        const response = await statusService.EditStatus(updatedStatus);
    
        if (response.success !== true) {
            Toast.Show("error", response.message);
            setLoadingUpdate(false);
            return;
        }
    
        Toast.Show("success", response.message);

        fetchData();

        setLoadingUpdate(false);
    };

    return (
        <Layout>
            <div className="pageContainer">
                <div className="pageHeader">
                    <PageTitle title={pageTitle} allowGoBack={true} />
                </div>
    
                <div className="pageBody">
                    <Row style={{ margin: "0px", padding: "0px", alignItems: "flex-start", alignContent: "flex-start" }}>
                        <Col xl={6}>
                            <label style={{ margin: "13px 0", padding: "0px" }}>
                                <b>Dados do Estado</b>
                            </label> 
                            <Row>
                                <InputComponent
                                    xl={12}
                                    lg={12}
                                    md={12}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="text"
                                    value={status.name}
                                    label={intl.formatMessage({ id: "Screens.Status.Info.Name" })}
                                    readOnly={true}
                                    onChange={(t: string) => {
                                        setStatus({ ...status, name: t });
                                    }}
                                />
                            </Row>
                            <Row>
                                <Col
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}
                                >
                                    <label style={{ fontSize: "13px", fontWeight: "500", marginBottom: "5px" }}>
                                        {intl.formatMessage({ id: "Screens.Status.Search.Pipe" })}
                                    </label>
                                    <select
                                        style={{
                                            width: "100%",
                                            height: "43px",
                                            borderRadius: "8px",
                                            border: "1px solid #ced4da",
                                            padding: "0px 12px",
                                            fontSize: "14px",
                                            color: "#495057",
                                            backgroundColor: "#fff",
                                        }}
                                        value={status.idPipe ?? ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setStatus({ ...status, idPipe: Number(value) });
                                        }}
                                    >
                                        <option value="">Selecione um processo</option>
                                        {pipes.map((pipe) => (
                                            <option key={pipe.id} value={pipe.id}>
                                                {pipe.name}
                                            </option>
                                        ))}
                                    </select>
                                </Col>
                                <InputComponent
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="text"
                                    value={status.statusEnumValue}
                                    label={intl.formatMessage({ id: "Screens.User.Info.StatusEnumValue" })}
                                    readOnly={false}
                                    onChange={(t: string) => {
                                        setStatus({ ...status, statusEnumValue: t });
                                    }}
                                />
                            </Row>
                            <Row>
                                 <InputComponent
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="text"
                                    value={status.frontendName}
                                    label={intl.formatMessage({ id: "Screens.User.Info.FrontendName" })}
                                    readOnly={false}
                                    onChange={(t: string) => {
                                        setStatus({ ...status, frontendName: t });
                                    }}
                                />
                                <InputComponent
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="number"
                                    value={status.frontendOrder}
                                    label={intl.formatMessage({ id: "Screens.User.Info.FrontendOrder" })}
                                    readOnly={false}
                                    onChange={(t: string) => {
                                        setStatus({ ...status, frontendOrder: Number(t) });
                                    }}
                                />
                            </Row>
                            <Row>
                                <InputComponent
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ width: "100%" }}
                                    type="text"
                                    value={status.color}
                                    label={intl.formatMessage({ id: "Screens.User.Info.Color" })}
                                    readOnly={false}
                                    onChange={(t: string) => {
                                        setStatus({ ...status, color: t });
                                    }}
                                />
                                <Col
                                    xl={6}
                                    lg={6}
                                    md={6}
                                    sm={12}
                                    xs={12}
                                    style={{ display: "flex", flexDirection: "column", marginBottom: "15px" }}
                                >
                                    <label style={{ fontSize: "13px", fontWeight: "500", marginBottom: "5px" }}>
                                        {intl.formatMessage({ id: "Screens.User.Info.Active" })}
                                    </label>
                                    <select
                                        style={{
                                            width: "100%",
                                            height: "43px",
                                            borderRadius: "8px",
                                            border: "1px solid #ced4da",
                                            padding: "0px 12px",
                                            fontSize: "14px",
                                            color: "#495057",
                                            backgroundColor: "#fff",
                                        }}
                                        value={status.active === true ? "true" : status.active === false ? "false" : ""}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            if (value === "true") {
                                                setStatus({ ...status, active: true });
                                            } else if (value === "false") {
                                                setStatus({ ...status, active: false });
                                            } else {
                                                setStatus({ ...status, active: undefined as any });
                                            }
                                        }}
                                    >
                                        <option value="true">Ativo</option>
                                        <option value="false">Inativo</option>
                                    </select>
                                </Col>
                            </Row>
                            <Row>
                                <Col xl={3} lg={3} md={3} sm={12} xs={12} style={{ marginTop: "1em", display: "inline-flex", gap: "1em" }}>
                                <ButtonComponent
                                    text={intl.formatMessage({ id: "Common.Buttons.Save" })}
                                    onClick={() => {
                                        editStatus();  
                                    }}
                                    disabled={loadingUpdate}
                                />
                                </Col>
                            </Row>
                        </Col>
    
                        <Col xl={6}>
                            <Row xl={1} lg={1} md={1} sm={12} xs={12}>
                                <StatusNavBar baseStatusName={baseStatusName} />
                            </Row>
                        </Col>
                    </Row>
                </div>
            </div>
        </Layout>
    );
}